import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, ShoppingBag, DollarSign, Calendar, Package, ArrowRight, Sparkles } from "lucide-react";

export default function CustomerDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    api.get("/my/dashboard").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [user, authLoading]);

  if (authLoading || loading) {
    return <div className="max-w-6xl mx-auto px-6 py-20"><Loader2 className="animate-spin text-[#D4AF37]" /></div>;
  }
  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <h1 className="font-serif-display text-3xl text-white">Sign in to view your dashboard</h1>
        <Link to="/login" className="btn-gold mt-6 inline-block px-6 py-3 rounded text-xs tracking-widest uppercase">Sign In</Link>
      </div>
    );
  }
  if (!data) return null;

  const stats = [
    { icon: ShoppingBag, label: "Total Orders", value: data.total_orders },
    { icon: DollarSign, label: "Total Spend", value: `£${Number(data.total_spent || 0).toFixed(2)}` },
    { icon: Package, label: "Products Purchased", value: data.products_purchased },
    { icon: Calendar, label: "Last Purchase", value: data.last_purchase_at ? new Date(data.last_purchase_at).toLocaleDateString() : "—" },
  ];

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16" data-testid="customer-dashboard">
      <div className="label-eyebrow mb-3">Member Dashboard</div>
      <h1 className="font-serif-display text-5xl text-white">Welcome back, <span className="italic text-[#D4AF37]">{(user.name || user.email).split(" ")[0]}</span></h1>
      <p className="text-neutral-400 mt-4">Your purchase journey with HOH_Vibes at a glance.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10" data-testid="dashboard-stats">
        {stats.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="luxury-card rounded-xl p-6">
              <Icon size={20} className="text-[#D4AF37]" />
              <div className="text-xs tracking-widest uppercase text-neutral-500 mt-3">{s.label}</div>
              <div className="font-serif-display text-3xl text-white mt-1">{s.value}</div>
            </div>
          );
        })}
      </div>

      {data.tags?.length > 0 && (
        <div className="mt-12 luxury-card rounded-xl p-6" data-testid="dashboard-tags">
          <div className="label-eyebrow mb-4 flex items-center gap-2"><Sparkles size={14} /> Your Interests</div>
          <div className="flex flex-wrap gap-2">
            {data.tags.map((t) => (
              <span key={t} className="px-3 py-1 text-[10px] tracking-widest uppercase rounded border border-[#D4AF37]/40 text-[#D4AF37]">{t}</span>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        {data.products?.length > 0 && (
          <div className="luxury-card rounded-xl p-6">
            <div className="label-eyebrow mb-4">Your Library</div>
            <ul className="space-y-2">
              {data.products.map((p, i) => (
                <li key={i} className="text-neutral-200 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />{p}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="luxury-card rounded-xl p-6">
          <div className="label-eyebrow mb-4">Recent Orders</div>
          {data.recent_orders?.length > 0 ? (
            <ul className="space-y-3">
              {data.recent_orders.map((o) => (
                <li key={o.id} className="flex justify-between border-b border-white/10 pb-2 last:border-0">
                  <div>
                    <div className="text-white text-sm">#{o.id.slice(0, 8)}</div>
                    <div className="text-xs text-neutral-500">{new Date(o.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="text-[#D4AF37] font-serif-display">£{Number(o.amount).toFixed(2)}</div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-neutral-500 text-sm">No purchases yet.</p>
          )}
          <Link to="/downloads" className="mt-4 inline-flex items-center gap-2 text-[#D4AF37] text-sm hover:text-[#FCE68A]">
            View all downloads <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
